/** The spectate socket, and the thing that keeps reopening it.
 *
 * One hook owns the whole conversation with `/v1/live/{user}`: it asks
 * whether the stream can be watched, opens the WebSocket, reads the object's
 * five kinds of message, and comes back when the socket drops. What it hands
 * the screen is deliberately split in two. Frames go into a ref, because four
 * a second through React state would re-render the page for a number that is
 * about to be interpolated anyway; everything else — who is driving, what
 * they are driving, the lap and sector state — is ordinary state, because it
 * changes rarely and the screen must re-render when it does.
 *
 * Reconnecting is a policy rather than a loop. Each attempt starts with the
 * probe in `connection.ts`, so a service with live switched off says so once
 * and stops, and a stream nobody is on is named as such rather than polled
 * behind a "reconnecting" notice. Only a socket that was actually open and is
 * not any more is called a reconnection — a refused upgrade closes the same
 * way, and the difference is the whole of the page's honesty about what it is
 * waiting for. The buffer is not cleared between attempts: a socket that
 * drops and returns within a second should carry on drawing the same lap, not
 * blank the map. It *is* cleared when the stream id changes, which is a
 * different driver and a different circuit.
 *
 * `useLiveListing` is here for the same reason: the spectator count and the
 * public flag are not on the socket — the object sends `spectators` only to
 * the broadcaster — so the page reads them from `/api/live`, occasionally,
 * while it is watching.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { getLiveListing, liveSocketUrl } from '@/lib/api';
import type { LiveFrame, LiveStream, LiveWireHello, LiveWireMessage } from '@/lib/types';

import { MAX_ATTEMPTS, REFUSED, backoffDelay, probeStream, shouldReconnect } from './connection';
import type { LiveStatus, LiveViewer } from './connection';
import { EMPTY_TIMING, foldFrame, measuredHz, pushSample, seedSamples } from './interpolate';
import type { LiveTiming, Sample } from './interpolate';

/** How often the spectator count is refreshed while watching. Slow, because
 *  it is a courtesy figure and every read is a row the service pays for. */
const LISTING_MS = 20_000;

// ── reading the wire ──────────────────────────────────────────────────────

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** A frame, rebuilt from checked values. The object already validated these,
 *  but a page that draws whatever arrives is a page that draws `NaN`. */
function readFrame(raw: unknown): LiveFrame | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const t = record['t'];
  const x = record['x'];
  const z = record['z'];
  if (typeof t !== 'number' || typeof x !== 'number' || typeof z !== 'number') return null;
  if (!Number.isFinite(t) || !Number.isFinite(x) || !Number.isFinite(z)) return null;
  return {
    t,
    x,
    z,
    speed: num(record['speed'], 0),
    gear: num(record['gear'], 0),
    lap: num(record['lap'], 0),
    lap_time: num(record['lap_time'], 0),
    sector: num(record['sector'], 0),
    on_track: record['on_track'] !== false,
    last_lap: typeof record['last_lap'] === 'number' ? record['last_lap'] : null,
    best_lap: typeof record['best_lap'] === 'number' ? record['best_lap'] : null,
  };
}

function parseMessage(text: string): LiveWireMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const record = parsed as Record<string, unknown>;

  switch (record['type']) {
    case 'hello':
      return {
        type: 'hello',
        user_id: str(record['user_id']),
        official_id: str(record['official_id']),
        track_name: str(record['track_name']),
        car: str(record['car']),
        started_at: str(record['started_at']),
        hz: num(record['hz'], 0),
        spectators: num(record['spectators'], 0),
        ring_seconds: num(record['ring_seconds'], 0),
      };
    case 'replay': {
      const raw = record['frames'];
      const frames = Array.isArray(raw)
        ? raw.map(readFrame).filter((frame): frame is LiveFrame => frame !== null)
        : [];
      return { type: 'replay', frames };
    }
    case 'frame': {
      const frame = readFrame(record['frame']);
      return frame ? { type: 'frame', frame } : null;
    }
    case 'meta':
      return {
        type: 'meta',
        official_id: str(record['official_id']),
        track_name: str(record['track_name']),
        car: str(record['car']),
      };
    case 'end':
      return { type: 'end', reason: str(record['reason']) };
    case 'spectators':
      return { type: 'spectators', n: num(record['n'], 0) };
    default:
      return null;
  }
}

// ── the hook ──────────────────────────────────────────────────────────────

export interface LiveConnection {
  status: LiveStatus;
  /** The service's own words: why it refused, or why the stream ended. */
  reason: string;
  hello: LiveWireHello | null;
  /** Corrected mid-stream by a `meta` message, so these are not `hello`'s. */
  officialId: string;
  trackName: string;
  car: string;
  timing: LiveTiming;
  /** The frame buffer. A ref, so sixty-hertz drawing costs no renders. */
  samples: { readonly current: Sample[] };
  /** Frames a second as measured here, not as advertised. */
  hz: number | null;
  /** Frames received since this page connected. */
  frames: number;
  /** Which reconnection attempt is outstanding; zero while connected. */
  attempt: number;
  /** True once the page has stopped asking on its own. */
  givenUp: boolean;
  retry: () => void;
}

export function useLiveStream(
  streamId: string | undefined,
  viewer?: LiveViewer | null,
): LiveConnection {
  // Pulled apart into primitives so that a caller building `{ id, admin }`
  // inline does not restart the socket on every render.
  const viewerId = viewer?.id ?? '';
  const viewerIsAdmin = viewer?.admin ?? false;

  const samples = useRef<Sample[]>([]);
  const [status, setStatus] = useState<LiveStatus>(streamId ? 'connecting' : 'idle');
  const [reason, setReason] = useState('');
  const [hello, setHello] = useState<LiveWireHello | null>(null);
  const [officialId, setOfficialId] = useState('');
  const [trackName, setTrackName] = useState('');
  const [car, setCar] = useState('');
  const [timing, setTiming] = useState<LiveTiming>(EMPTY_TIMING);
  const [hz, setHz] = useState<number | null>(null);
  const [frames, setFrames] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [givenUp, setGivenUp] = useState(false);
  const [nonce, setNonce] = useState(0);

  const retry = useCallback(() => {
    setGivenUp(false);
    setNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!streamId) {
      setStatus('idle');
      return;
    }

    let alive = true;
    let socket: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;

    samples.current = [];
    setTiming(EMPTY_TIMING);
    setHello(null);
    setOfficialId('');
    setTrackName('');
    setCar('');
    setHz(null);
    setFrames(0);
    setAttempt(0);
    setGivenUp(false);
    setReason('');
    setStatus('connecting');

    /**
     * Schedule another attempt.
     *
     * `dropped` is the difference between a socket that was open and is not
     * any more — which is a reconnection, and says so — and a stream that
     * never opened because nobody was driving there. The second keeps the
     * reason the probe gave, because "reconnecting" would be a claim that
     * there was once a connection.
     */
    const again = (dropped: boolean) => {
      if (!alive) return;
      tries += 1;
      if (tries > MAX_ATTEMPTS) {
        setGivenUp(true);
        return;
      }
      setAttempt(tries);
      if (dropped) setStatus('reconnecting');
      timer = setTimeout(() => {
        void open();
      }, backoffDelay(tries, Math.random()));
    };

    const onFrame = (frame: LiveFrame) => {
      const at = Date.now();
      samples.current = pushSample(samples.current, { at, frame });
      setTiming((previous) => foldFrame(previous, frame));
      setFrames((count) => count + 1);
      const measured = measuredHz(samples.current);
      if (measured !== null) {
        setHz((previous) =>
          previous !== null && Math.abs(previous - measured) < 0.05 ? previous : measured,
        );
      }
      setStatus('live');
    };

    const listen = () => {
      const url = liveSocketUrl(streamId);
      let opened: WebSocket;
      try {
        opened = new WebSocket(url);
      } catch {
        setStatus(REFUSED.status);
        setReason(REFUSED.reason);
        again(false);
        return;
      }
      socket = opened;

      // Whether this socket ever carried anything. An upgrade the Worker
      // refused closes exactly like a connection that dropped, and the
      // difference is the difference between "reconnecting" and a claim the
      // page is not entitled to make.
      let everOpen = false;
      opened.onopen = () => {
        everOpen = true;
      };

      opened.onmessage = (event: MessageEvent) => {
        if (!alive) return;
        if (typeof event.data !== 'string') return;
        const message = parseMessage(event.data);
        if (!message) return;

        switch (message.type) {
          case 'hello':
            tries = 0;
            setAttempt(0);
            setHello(message);
            setOfficialId(message.official_id);
            setTrackName(message.track_name);
            setCar(message.car);
            break;
          case 'replay':
            if (message.frames.length > 0) {
              samples.current = seedSamples(message.frames, Date.now());
              // The replay is the last few seconds of a lap already under
              // way, so the timing state is caught up from it rather than
              // starting the strip at zero.
              setTiming(message.frames.reduce(foldFrame, EMPTY_TIMING));
              setFrames(message.frames.length);
              setStatus('live');
            }
            break;
          case 'frame':
            onFrame(message.frame);
            break;
          case 'meta':
            setOfficialId(message.official_id);
            setTrackName(message.track_name);
            setCar(message.car);
            break;
          case 'end':
            setReason(message.reason || 'the driver stopped streaming');
            setStatus('ended');
            alive = false;
            opened.close();
            break;
          case 'spectators':
            break;
        }
      };

      opened.onclose = (event: CloseEvent) => {
        if (!alive) return;
        socket = null;
        if (!shouldReconnect(event.code)) {
          setReason(event.reason || 'the stream has ended');
          setStatus('ended');
          return;
        }
        if (!everOpen) {
          setStatus(REFUSED.status);
          setReason(REFUSED.reason);
        }
        again(everOpen);
      };

      // `onerror` carries nothing a person could read, and a close always
      // follows it, so the reconnection is left to `onclose`.
      opened.onerror = () => undefined;
    };

    const open = async () => {
      if (!alive) return;
      if (tries === 0) setStatus('connecting');
      const seat = viewerId ? { id: viewerId, admin: viewerIsAdmin } : null;
      const fault = await probeStream(streamId, seat);
      if (!alive) return;
      if (fault) {
        setStatus(fault.status);
        setReason(fault.reason);
        if (fault.retry) again(false);
        else setGivenUp(true);
        return;
      }
      listen();
    };

    void open();

    return () => {
      alive = false;
      if (timer !== undefined) clearTimeout(timer);
      if (socket) {
        socket.onclose = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.close();
      }
    };
  }, [streamId, viewerId, viewerIsAdmin, nonce]);

  return {
    status,
    reason,
    hello,
    officialId,
    trackName,
    car,
    timing,
    samples,
    hz,
    frames,
    attempt,
    givenUp,
    retry,
  };
}

// ── who is on air ─────────────────────────────────────────────────────────

export interface LiveListingState {
  streams: LiveStream[];
  /** False when the admin has switched the live type off entirely. */
  enabled: boolean;
  loading: boolean;
  error: unknown;
  reload: () => void;
}

/**
 * `GET /api/live`, once and then occasionally.
 *
 * Note the shape: the endpoint answers `{ enabled, streams }`, not the
 * `{ items }` envelope the rest of the API uses, which is why this goes
 * through `getLiveListing` rather than `listLive`.
 */
export function useLiveListing(pollMs: number = LISTING_MS): LiveListingState {
  const [streams, setStreams] = useState<LiveStream[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    let alive = true;

    const read = async () => {
      try {
        const listing = await getLiveListing();
        if (!alive) return;
        setStreams(listing.streams ?? []);
        setEnabled(listing.enabled !== false);
        setError(null);
      } catch (thrown) {
        if (!alive) return;
        setError(thrown);
      } finally {
        if (alive) setLoading(false);
      }
    };

    void read();
    const timer = pollMs > 0 ? setInterval(() => void read(), pollMs) : undefined;
    return () => {
      alive = false;
      if (timer !== undefined) clearInterval(timer);
    };
  }, [pollMs, nonce]);

  return { streams, enabled, loading, error, reload };
}
